import React, { memo, useMemo } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import type { ThemeColors } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import type { SvgIconName } from '../../components/common/SvgIcon';
import { journeyListItemEntering } from '../../shared/theme/studio/journeyMotion';
import { JourneyStepIntro } from './JourneyStepIntro';
import { JourneyOptionCard, type JourneyOptionEmphasis } from './JourneyOptionCard';
import type { JourneyIconMotion } from './JourneyIconMotionWrap';

export type JourneyOptionItem = {
  id: string;
  labelKey: string;
  subtitleKey: string;
  icon: SvgIconName;
  tone: 'error' | 'success' | 'secondary' | 'primary';
  icon_motion?: JourneyIconMotion;
};

type Props = {
  colors: ThemeColors;
  title: string;
  subtitle: string;
  options: JourneyOptionItem[];
  selected_id: string;
  on_select: (id: string) => void;
  accessibility_label: string;
  test_id_prefix: string;
  emphasis?: JourneyOptionEmphasis;
};

/** Shared list step for journey goal + activity selection. */
export const JourneyOptionStep = memo(function JourneyOptionStep({
  colors,
  title,
  subtitle,
  options,
  selected_id,
  on_select,
  accessibility_label,
  test_id_prefix,
  emphasis = 'default',
}: Props) {
  const { t } = useTranslation();
  const has_selection = Boolean(selected_id);

  const press_handlers = useMemo(() => {
    const map: Record<string, () => void> = {};
    for (const option of options) {
      map[option.id] = () => on_select(option.id);
    }
    return map;
  }, [options, on_select]);

  return (
    <View accessibilityRole="radiogroup" accessibilityLabel={accessibility_label}>
      <JourneyStepIntro colors={colors} title={title} subtitle={subtitle} />
      {options.map((option, index) => {
        const selected = selected_id === option.id;
        return (
          <Animated.View key={option.id} entering={journeyListItemEntering(index)}>
            <JourneyOptionCard
              colors={colors}
              title={t(option.labelKey)}
              subtitle={t(option.subtitleKey)}
              icon={option.icon}
              tone={option.tone}
              selected={selected}
              dimmed={has_selection && !selected}
              emphasis={emphasis}
              icon_motion={option.icon_motion ?? 'none'}
              onPress={press_handlers[option.id]!}
              testID={`${test_id_prefix}-${option.id}`}
            />
          </Animated.View>
        );
      })}
    </View>
  );
});
